import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PaginationMeta } from './common';

// --- Types ---

export type PaymentProvider = 'TOSS_PAYMENTS' | 'STRIPE' | 'BETA';
export type SubscriptionPlan = 'FREE' | 'PRO' | 'PERSONAL_PRO' | 'TEAM_PRO';
export type SubscriptionStatus = 'ACTIVE' | 'CANCELLED' | 'PAST_DUE' | 'TRIALING' | 'EXPIRED';
export type PaymentStatus = 'PENDING' | 'PAID' | 'FAILED';
export type PaymentCurrency = 'KRW' | 'USD';

export type Subscription = {
  id: string;
  memberId: string;
  teamId: string | null;
  plan: SubscriptionPlan;
  seats: number;
  status: SubscriptionStatus;
  provider: PaymentProvider;
  providerBillingKey: string | null;
  providerSubscriptionId: string | null;
  currentPeriodStartAt: string | null;
  currentPeriodEndAt: string | null;
  trialEndAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SubscriptionWithPayments = Subscription & {
  recentPayments: Payment[];
};

export type Payment = {
  id: string;
  subscriptionId: string;
  memberId: string;
  amount: number;
  currency: PaymentCurrency;
  status: PaymentStatus;
  provider: PaymentProvider;
  orderId: string;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SeatProrationPreview = {
  currentSeats: number;
  newSeats: number;
  seatDelta: number;
  currency: PaymentCurrency;
  unitAmount: number;
  proratedAmount: number;
  remainingDays: number;
  totalDays: number;
  prorationMode: 'immediate' | 'next_cycle';
  currentPeriodEndAt: string;
};

type SubscriptionResponse = { data: SubscriptionWithPayments | null };
type SubscribeResponse = { data: { subscription: Subscription; payment: Payment | null } };
type StartTrialResponse = { data: { subscription: Subscription } };
type CancelResponse = { data: Subscription };
type PaymentsResponse = { data: Payment[]; meta: PaginationMeta };
export type BillingInfo = {
  providers: PaymentProvider[];
  defaultProvider: PaymentProvider | null;
  pricing: {
    personal: {
      monthlyKrw: number;
      yearlyKrw: number;
      monthlyUsd: number;
      yearlyUsd: number;
    };
    team: {
      seatMonthlyKrw: number;
      seatYearlyKrw: number;
      seatMonthlyUsd: number;
      seatYearlyUsd: number;
      minSeats: number;
    };
  };
  trial: {
    personalEnabled: boolean;
    teamEnabled: boolean;
    teamOnCreateEnabled: boolean;
    days: number;
  };
};

type ProvidersResponse = { data: BillingInfo };
type SeatProrationPreviewResponse = { data: SeatProrationPreview };
type UpdateTeamSubscriptionSeatsResponse = { data: Subscription };

// --- Billing API ---

export const fetchSubscription = async (
  token: string,
  teamId?: string | null,
): Promise<SubscriptionWithPayments | null> => {
  const query = new URLSearchParams();
  if (teamId) query.set('teamId', teamId);

  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/billing/subscription${query.toString() ? `?${query.toString()}` : ''}`,
    {
      cache: 'no-store',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch subscription: ${response.status}`);
  }

  return ((await response.json()) as SubscriptionResponse).data;
};

export const subscribePro = async (
  token: string,
  input: {
    provider: PaymentProvider;
    plan?: Extract<SubscriptionPlan, 'PERSONAL_PRO' | 'TEAM_PRO'>;
    period?: 'monthly' | 'yearly';
    teamId?: string;
    seats?: number;
    authData: Record<string, unknown>;
  },
): Promise<{ subscription: Subscription; payment: Payment | null }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/billing/subscribe`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to subscribe: ${response.status}`);
  }

  return ((await response.json()) as SubscribeResponse).data;
};

export const startTrial = async (
  token: string,
  input: {
    plan?: Extract<SubscriptionPlan, 'PERSONAL_PRO' | 'TEAM_PRO'>;
    teamId?: string;
    seats?: number;
  } = {},
): Promise<{ subscription: Subscription }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/billing/start-trial`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to start trial: ${response.status}`);
  }

  return ((await response.json()) as StartTrialResponse).data;
};

export const cancelSubscription = async (token: string, teamId?: string | null): Promise<Subscription> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/billing/cancel`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(teamId ? { teamId } : {}),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to cancel subscription: ${response.status}`);
  }

  return ((await response.json()) as CancelResponse).data;
};

export const fetchPayments = async (
  token: string,
  params?: { page?: number; limit?: number; teamId?: string | null },
): Promise<{ data: Payment[]; meta: PaginationMeta }> => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.teamId) queryParams.set('teamId', params.teamId);

  const qs = queryParams.toString();
  const url = `${getApiUrl()}/api/billing/payments${qs ? `?${qs}` : ''}`;

  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch payments: ${response.status}`);
  }

  return (await response.json()) as PaymentsResponse;
};

export const fetchBillingInfo = async (token: string): Promise<BillingInfo> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/billing/providers`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch billing info: ${response.status}`);
  }

  return ((await response.json()) as ProvidersResponse).data;
};

export const previewTeamSubscriptionSeats = async (
  token: string,
  teamId: string,
  seats: number,
): Promise<SeatProrationPreview> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}/subscription/preview`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ seats }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to preview seats: ${response.status}`);
  }

  return ((await response.json()) as SeatProrationPreviewResponse).data;
};

export const updateTeamSubscriptionSeats = async (
  token: string,
  teamId: string,
  seats: number,
): Promise<Subscription> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/teams/${teamId}/subscription/seats`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ seats }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update seats: ${response.status}`);
  }

  return ((await response.json()) as UpdateTeamSubscriptionSeatsResponse).data;
};
